/**
 * @fileoverview Linked Account Task for Apple in the Enterprise Launch Flow.
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */

import {AppleLinkedAccount} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/apple/applelinkedaccount';
import {AppleConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/appleconfiguration';
import {EnterpriseConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/enterpriseconfiguration';
import {Task} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/task';
import {TaskIds} from '/src/com/yext/listingslaunchstorm/js/enterprise/taskflow/data/taskids';

import AppleLogoLargeSvg from '/src/com/yext/listingslaunchstorm/public/icons/apple-logo-large.svg';
import AppleLogoSvg from '/src/com/yext/listingslaunchstorm/public/icons/apple-logo.svg';
import BookSvg from '/src/com/yext/listingslaunchstorm/public/icons/book.svg';

export const AppleLinkedAccountTask = publisherId => new Task({
  id: TaskIds.APPLE_LINKED_ACCOUNT,

  getCard: (_enterpriseConfiguration, _index, selectedPageId, setSelectedPageId, disabled) => ({
    id: TaskIds.APPLE_LINKED_ACCOUNT,
    title: yext.msg('Linked Accounts'),
    icon: <AppleLogoSvg />,
    isSelected: TaskIds.APPLE_LINKED_ACCOUNT === selectedPageId,
    onClick: disabled ? undefined : () => setSelectedPageId(TaskIds.APPLE_LINKED_ACCOUNT),
  }),

  getPage: (enterpriseConfiguration, setEnterpriseConfiguration, pageControls) => ({
    id: TaskIds.APPLE_LINKED_ACCOUNT,
    layoutHeader: {
      icon: <AppleLogoLargeSvg />,
      title: yext.msg('Apple'),
      description: yext.msg('Link your Apple account. Linking your account allows you to manage your Apple listings through this platform.'),
      resourceLink: {
        src: 'https://hitchhikers.yext.com/docs/listings/apple-integration-overview/',
        external: true,
        displayText: yext.msg('Learn About Apple Integration'),
        icon: <BookSvg />,
      },
    },

    content: <AppleLinkedAccount
      appleConfiguration={enterpriseConfiguration.appleConfiguration ?? AppleConfiguration.initialConfiguration(publisherId)}
      setAppleConfiguration={appleConfiguration => setEnterpriseConfiguration(
        new EnterpriseConfiguration({...enterpriseConfiguration, appleConfiguration}),
      )}
    />,
  }),

  isComplete: enterpriseConfiguration => (
    isConfigurationComplete(enterpriseConfiguration.appleConfiguration)
  ),
  isSectionSkipped: enterpriseConfiguration => !!enterpriseConfiguration?.appleConfiguration?.skipSetup,
});

/**
 * Completing Apple Configuration is possible via:
 * 1. Selecting businessIds for each country or excluding the country
 * 2. Skipping Apple Setup
 *
 * @param {AppleConfiguration | undefined} appleConfiguration
 * @returns {boolean}
 */
function isConfigurationComplete(appleConfiguration) {
  if (!!appleConfiguration && (appleConfiguration.skipSetup || appleConfiguration.delegateAppleAccount)) {
    return true;
  }
  if (!appleConfiguration || !appleConfiguration.linkedAccount || !appleConfiguration.appleCountryInfos) {
    return false;
  }

  return appleConfiguration.appleCountryInfos.every(appleCountryInfo => {
    return (
      (appleCountryInfo.selectedAppleBusiness
        && appleCountryInfo.selectedAppleBusiness.appleBusinessId)
      || appleCountryInfo.excluded
    );
  });
}
